import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { captureException } from '@sentry/react';
import { CustomError } from 'typescript/utilities/custom_error';
import {
  listOffers as clientListOffers,
  selectOffer as clientSelectOffer,
} from 'typescript/clients/solidshop/offers_client';
import { MUTATION_KEYS, QUERY_KEYS } from 'typescript/constants/react_query';
import { CountryCode, LanguageCode } from 'typescript/constants/localization';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { Offer, ListOffersResponse } from 'typescript/types/api/offers';

const usePrefetchOffers = ({
  orderNumber,
  countryCode,
  languageCode,
}: {
  orderNumber: string;
  countryCode?: CountryCode;
  languageCode?: LanguageCode;
}) => {
  const { countryCode: contextCountryCode, languageCode: contextLanguageCode } =
    useLocaleInfo();
  const queryClient = useQueryClient();

  const prefetchOffers = () => {
    queryClient.prefetchQuery({
      queryKey: [QUERY_KEYS.OFFERS, { orderNumber }],
      queryFn: () =>
        clientListOffers({
          orderNumber,
          countryCode: countryCode ?? contextCountryCode,
          languageCode: languageCode ?? contextLanguageCode,
        }),
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 5,
    });
  };
  return { prefetchOffers };
};

const useListOffers = ({
  orderNumber,
  countryCode,
  languageCode,
  enabled = true,
}: {
  orderNumber: string;
  countryCode?: CountryCode;
  languageCode?: LanguageCode;
  enabled?: boolean;
}) => {
  const { countryCode: contextCountryCode, languageCode: contextLanguageCode } =
    useLocaleInfo();

  const { data: offersData, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.OFFERS, { orderNumber }],
    queryFn: () =>
      clientListOffers({
        orderNumber,
        countryCode: countryCode ?? contextCountryCode,
        languageCode: languageCode ?? contextLanguageCode,
      }),
    enabled,
  });

  return { offersData, ...rest };
};

const useSelectOffer = ({ orderNumber }: { orderNumber: string }) => {
  const { countryCode: contextCountryCode, languageCode: contextLanguageCode } =
    useLocaleInfo();
  const queryClient = useQueryClient();
  const mutationKey = [MUTATION_KEYS.OFFER_SELECT, { orderNumber }];

  const { mutate: selectOffer, ...rest } = useMutation({
    mutationKey,
    mutationFn: ({
      selectedOfferToken,
      countryCode = contextCountryCode,
      languageCode = contextLanguageCode,
    }: {
      selectedOfferToken: string;
      countryCode?: CountryCode;
      languageCode?: LanguageCode;
    }) =>
      clientSelectOffer({
        languageCode,
        orderNumber,
        selectedOfferToken,
        countryCode,
      }),
    onMutate: async ({ selectedOfferToken }) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.OFFERS, { orderNumber }],
      });

      const previousOffers: ListOffersResponse = queryClient.getQueryData([
        QUERY_KEYS.OFFERS,
        { orderNumber },
      ]);

      queryClient.setQueryData(
        [QUERY_KEYS.OFFERS, { orderNumber }],
        (old: ListOffersResponse) => {
          return {
            offers: old.offers.map((offer: Offer) => ({
              ...offer,
              selected: offer.token === selectedOfferToken,
            })),
          };
        },
      );

      return { previousOffers };
    },
    onError: (error, __, context) => {
      if (context?.previousOffers) {
        queryClient.setQueryData(
          [QUERY_KEYS.OFFERS, { orderNumber }],
          context.previousOffers,
        );
      }

      captureException(new CustomError(MUTATION_KEYS.OFFER_SELECT, error), {
        extra: {
          orderNumber,
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.OFFERS, { orderNumber }],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDER_SUMMARY, { orderNumber }],
      });
    },
  });

  const isAnyPending =
    useIsMutating({
      mutationKey,
    }) > 0;

  return { selectOffer, isAnyPending, ...rest };
};

export { usePrefetchOffers, useListOffers, useSelectOffer };
